import React from 'react';
import {
  Card,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Box,
  Stack,
  Avatar,
  Chip,
  InputAdornment,
  OutlinedInput,
  Tooltip,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import CustomPagination from 'src/components/CustomPagination';
import { financialSimulationService } from 'src/services/financial-simulation.service';
import { useAuth } from 'src/context/auth/AuthContext';
import { useDebounce } from 'src/hooks/useDebounce';

function getStatusChip(status?: string) {
  switch (status) {
    case 'Completed':
      return {
        label: 'Completed',
        color: '#059669',
        bgcolor: 'rgba(16, 185, 129, 0.12)',
        icon: 'solar:check-circle-bold-duotone',
      };
    case 'Overdue':
      return {
        label: 'Overdue',
        color: '#DC2626',
        bgcolor: 'rgba(239, 68, 68, 0.12)',
        icon: 'solar:alarm-bold-duotone',
      };
    case 'In Progress':
    default:
      return {
        label: 'In Progress',
        color: '#D97706',
        bgcolor: 'rgba(245, 158, 11, 0.12)',
        icon: 'solar:hourglass-line-bold-duotone',
      };
  }
}

export function StudentChallenges() {
  const navigate = useNavigate();
  const { user, hasPermission, getBaseRoute } = useAuth();
  const assignChallengeRoute = getBaseRoute('/assign-new-chellenge');
  const viewSimulationRoute = getBaseRoute('/view-financial-simulations');

  const canViewAll = hasPermission('whammies.view_all');
  const canAssign = hasPermission('whammies.assign');

  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [searchText, setSearchText] = React.useState('');
  const [selectedStatus, setSelectedStatus] = React.useState<string | null>(null);

  const debouncedSearch = useDebounce(searchText, 300);

  const {
    data: challenges,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      'challenges',
      {
        page,
        rowsPerPage,
        search: debouncedSearch,
        status: selectedStatus,
        teacherUserId: !canViewAll ? (user?.id || user?.email) : undefined,
      },
    ],
    queryFn: () =>
      financialSimulationService.getChallenges({
        page,
        limit: rowsPerPage,
        search: debouncedSearch,
        status: selectedStatus as 'In Progress' | 'Completed' | 'Overdue',
        teacherUserId: !canViewAll ? (user?.id || user?.email) : undefined,
      }),
  });

  const challengeList = challenges?.data ?? [];

  const handlePageChange = (newPage: number) => setPage(newPage);

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const statusFilters = [
    { label: 'All Challenges', value: null },
    { label: 'In Progress', value: 'In Progress' },
    { label: 'Completed', value: 'Completed' },
    { label: 'Overdue', value: 'Overdue' },
  ];

  return (
    <Card
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        overflow: 'hidden',
      }}
    >
      {/* Filter & Search Bar */}
      <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          {/* Search Input */}
          <OutlinedInput
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(1);
            }}
            placeholder="Search by scholar or scenario..."
            startAdornment={
              <InputAdornment position="start">
                <Icon icon="solar:magnifer-linear" width={16} style={{ color: '#94A3B8' }} />
              </InputAdornment>
            }
            sx={{
              height: 36,
              fontSize: '0.8125rem',
              borderRadius: 1.75,
              maxWidth: { xs: '100%', md: 320 },
              bgcolor: 'grey.100',
              '& fieldset': { border: 'none' },
              '&:hover': { bgcolor: 'grey.200' },
            }}
          />

          {/* Status Chips Filter */}
          <Stack direction="row" spacing={0.75} sx={{ overflowX: 'auto', pb: { xs: 0.5, md: 0 } }}>
            {statusFilters.map((filter) => {
              const isSelected = selectedStatus === filter.value;
              return (
                <Chip
                  key={filter.label}
                  label={filter.label}
                  onClick={() => {
                    setSelectedStatus(filter.value);
                    setPage(1);
                  }}
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    height: 28,
                    px: 0.5,
                    borderRadius: 1.5,
                    cursor: 'pointer',
                    bgcolor: isSelected ? 'primary.main' : 'grey.100',
                    color: isSelected ? 'primary.contrastText' : 'text.secondary',
                    '&:hover': {
                      bgcolor: isSelected ? 'primary.dark' : 'grey.200',
                    },
                  }}
                />
              );
            })}
          </Stack>
        </Stack>
      </Box>

      {/* Table Section */}
      <TableContainer>
        <Table sx={{ minWidth: 700 }}>
          <TableHead sx={{ bgcolor: 'grey.50' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 1.5, fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Scholar</TableCell>
              <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 1.5, fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Scenario</TableCell>
              <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 1.5, fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Duration</TableCell>
              <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 1.5, fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Penalty Due</TableCell>
              <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 1.5, fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Status</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary', py: 1.5, fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={30} thickness={4} />
                  <Typography variant="caption" sx={{ mt: 1.25, color: 'text.secondary', fontWeight: 600, display: 'block', fontSize: '0.8125rem' }}>
                    Loading Whammy challenges...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : challengeList.length > 0 ? (
              challengeList.map((row: any) => {
                const studentName = `${row?.student?.user?.firstname || ''} ${row?.student?.user?.lastname || ''}`.trim() || 'Unnamed Scholar';
                const statusMeta = getStatusChip(row?.status);
                const amount = Number(row?.amount) || 0;
                const scholarId = row?.student?.id || row?.student_id;
                const viewStudentPath = scholarId ? getBaseRoute(`/view-student/${scholarId}`) : null;

                return (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      transition: 'background-color 0.15s ease',
                      '&:last-child td, &:last-child th': { border: 0 },
                    }}
                  >
                    {/* Scholar Info */}
                    <TableCell sx={{ py: 2 }}>
                      <Tooltip title={viewStudentPath ? 'View Scholar Profile' : ''}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1.5}
                          onClick={(e) => {
                            if (viewStudentPath) {
                              e.stopPropagation();
                              navigate(viewStudentPath);
                            }
                          }}
                          sx={{
                            cursor: viewStudentPath ? 'pointer' : 'default',
                            width: 'fit-content',
                            '&:hover .scholar-name': {
                              color: viewStudentPath ? 'primary.main' : 'text.primary',
                              textDecoration: viewStudentPath ? 'underline' : 'none',
                            },
                            '&:hover .scholar-avatar': {
                              transform: viewStudentPath ? 'scale(1.08)' : 'none',
                              boxShadow: viewStudentPath ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                            },
                          }}
                        >
                          <Avatar
                            className="scholar-avatar"
                            sx={{
                              width: 36,
                              height: 36,
                              bgcolor: 'primary.light',
                              color: 'primary.dark',
                              fontWeight: 800,
                              fontSize: '0.875rem',
                              transition: 'all 0.15s ease',
                            }}
                          >
                            {studentName.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography
                              className="scholar-name"
                              variant="subtitle2"
                              sx={{
                                fontWeight: 800,
                                color: 'text.primary',
                                fontSize: '0.875rem',
                                lineHeight: 1.25,
                                transition: 'color 0.15s ease',
                              }}
                            >
                              {studentName}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                              {row?.student?.user?.email || 'No email'}
                            </Typography>
                          </Box>
                        </Stack>
                      </Tooltip>
                    </TableCell>

                    {/* Scenario Title */}
                    <TableCell sx={{ py: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: 1.5,
                            bgcolor: 'rgba(237, 180, 46, 0.12)',
                            color: '#D97706',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Icon icon="solar:danger-triangle-bold-duotone" width={19} />
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.875rem', lineHeight: 1.25 }}>
                            {row?.scenario?.title || 'Custom Challenge'}
                          </Typography>
                          {row?.scenario?.description && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                fontSize: '0.75rem',
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                maxWidth: 280,
                              }}
                            >
                              {row?.scenario?.description}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </TableCell>

                    {/* Duration */}
                    <TableCell sx={{ py: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Icon icon="solar:calendar-linear" width={16} style={{ color: '#94A3B8' }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
                          {row?.duration ? `${row.duration} Days` : 'N/A'}
                        </Typography>
                      </Stack>
                    </TableCell>

                    {/* Amount */}
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#DC2626', fontSize: '0.9375rem' }}>
                        ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </TableCell>

                    {/* Status Chip */}
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        icon={<Icon icon={statusMeta.icon} width={14} />}
                        label={statusMeta.label}
                        size="small"
                        sx={{
                          height: 26,
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          color: statusMeta.color,
                          bgcolor: statusMeta.bgcolor,
                          border: `1px solid ${statusMeta.color}22`,
                          '& .MuiChip-icon': {
                            color: `${statusMeta.color} !important`,
                            marginLeft: '4px',
                          },
                        }}
                      />
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="right" sx={{ py: 2 }}>
                      <Tooltip title="View Whammy Details">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`${viewSimulationRoute}/${row.id}`)}
                          sx={{
                            p: 0.75,
                            bgcolor: 'grey.100',
                            color: 'text.secondary',
                            '&:hover': {
                              bgcolor: 'primary.light',
                              color: 'primary.dark',
                            },
                          }}
                        >
                          <Icon icon="solar:eye-bold-duotone" width={18} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Box sx={{ maxWidth: 320, mx: 'auto', textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        bgcolor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 1.5,
                        color: 'text.secondary',
                      }}
                    >
                      <Icon icon="solar:shield-warning-bold-duotone" width={24} />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.25 }}>
                      No Whammies Found
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', mb: 2, display: 'block' }}>
                      {searchText
                        ? `No challenges matching "${searchText}".`
                        : 'No active or completed Whammy challenges yet.'}
                    </Typography>
                    {canAssign && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Icon icon="solar:add-circle-bold" width={16} />}
                        onClick={() => navigate(assignChallengeRoute)}
                        sx={{
                          borderRadius: 1.75,
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          textTransform: 'none',
                          px: 1.75,
                          py: 0.5,
                        }}
                      >
                        Assign Challenge
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {challenges && challenges.total > 0 && (
        <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <CustomPagination
            count={challenges.total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Box>
      )}
    </Card>
  );
}
